import React from "react";

import { SimplifiedOptionSelector } from "./SimplifiedOptionSelector";

import { NewTOptions } from "@/interfaces/interfaces";

interface OptionSelectorMemoProps<T> {
  activeId: string | null | undefined;
  disabled: boolean;
  optionList: NewTOptions<T>[];
  setIsOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  onValueChange: (value: string | string[]) => Promise<void>;
  statusFor: string;
  config?: {
    isRequired?: boolean;
    isFinal?: boolean;
  };
  isMultiple?: boolean;
  placeholder?: string;
  className?: string;
  showIcon?: boolean;
  isOptionAdditional?: () => void;
}

// Enhanced option type with pre-computed styles
export interface EnhancedOption<T> extends NewTOptions<T> {
  computedBackgroundColor: string;
  computedTextOpacity: number;
  computedTextColor: string;
}

// Helper functions for stable color calculations
const getStableOpacity = (opacity: number | undefined): string => {
  if (opacity === undefined || opacity === null) {
    return "FF";
  }
  if (opacity === 100) {
    return "FF";
  }
  const opacityStr = Math.round((opacity * 255) / 100)
    .toString(16)
    .toUpperCase();

  return opacityStr.padStart(2, "0");
};

const getStableBackgroundColor = (
  backgroundColor: string | undefined,
  opacity: string,
): string => {
  if (
    !backgroundColor ||
    backgroundColor === "undefined" ||
    backgroundColor === "null"
  ) {
    return "transparent";
  }

  return backgroundColor === "transparent"
    ? "transparent"
    : backgroundColor + opacity;
};

export const OptionSelectorMemo = React.memo(
  <T,>({
    activeId,
    disabled,
    optionList,
    setIsOption,
    onValueChange,
    statusFor,
    config,
    isMultiple = false,
    placeholder,
    className,
    showIcon,
    isOptionAdditional,
  }: OptionSelectorMemoProps<T>): React.JSX.Element => {
    // Memoize stable activeId
    const activeIdMemo = React.useMemo(() => {
      return activeId || "";
    }, [activeId]);

    // Memoize and enhance optionList with pre-computed styles
    const enhancedOptionList = React.useMemo((): EnhancedOption<T>[] => {
      if (!optionList?.length) {
        return [];
      }

      return optionList.map((option) => {
        const opacity = getStableOpacity(option?.details?.backgroundOpacity);
        const computedBackgroundColor = getStableBackgroundColor(
          option?.details?.backgroundColor,
          opacity,
        );

        return {
          ...option,
          computedBackgroundColor,
          computedTextOpacity: (option?.details?.textOpacity || 100) / 100,
          computedTextColor: option?.details?.textColor || "#ffffff",
        };
      });
    }, [optionList]);

    // Memoize selected option(s) with styles
    const selectedOptionWithStyles = React.useMemo(() => {
      if (!enhancedOptionList.length || !activeIdMemo) {
        return null;
      }

      if (isMultiple) {
        const activeIds = Array.isArray(activeId) ? activeId : [activeIdMemo];

        return enhancedOptionList.filter((option) =>
          activeIds.includes(option?.id || ""),
        );
      }

      return (
        enhancedOptionList.find((option) => option.id === activeIdMemo) || null
      );
    }, [enhancedOptionList, activeIdMemo, activeId, isMultiple]);

    // Memoize filtered and sorted options for dropdown
    const sortedOptionsForDropdown = React.useMemo(() => {
      return enhancedOptionList.sort(
        (a, b) => (a?.details?.position || 0) - (b?.details?.position || 0),
      );
    }, [enhancedOptionList]);

    // Stable onValueChange wrapper
    const stableOnValueChange = React.useCallback(
      async (value: string | string[]) => {
        try {
          await onValueChange(value);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log("Failed to update option:", error);
          throw error;
        }
      },
      [onValueChange],
    );

    // If no options available, show loading state
    // if (!enhancedOptionList.length) {
    //   return (
    //     <div className='min-w-[200px] min-h-10 flex items-center justify-center'>
    //       <div className='w-32 h-8 bg-gray-200 rounded animate-pulse'></div>
    //     </div>
    //   );
    // }

    return (
      <SimplifiedOptionSelector
        activeId={activeIdMemo}
        disabled={disabled}
        optionList={enhancedOptionList}
        setIsOption={setIsOption}
        onValueChange={stableOnValueChange}
        statusFor={statusFor}
        config={config}
        isMultiple={isMultiple}
        placeholder={placeholder}
        className={className}
        showIcon={showIcon}
        isOptionAdditional={isOptionAdditional}
        // Pass pre-computed data to prevent recalculation
        selectedOptionWithStyles={selectedOptionWithStyles}
        sortedOptionsForDropdown={sortedOptionsForDropdown}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for better memoization
    return (
      prevProps.activeId === nextProps.activeId &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.optionList === nextProps.optionList &&
      prevProps.statusFor === nextProps.statusFor &&
      prevProps.isMultiple === nextProps.isMultiple &&
      JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
    );
  },
);

OptionSelectorMemo.displayName = "OptionSelectorMemo";

export default OptionSelectorMemo;
